import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { DataTable } from '@/components/data/DataTable'
import { ColumnDef } from '@tanstack/react-table'
import { FileJson } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { formatBytes } from '@/features/objects/utils/formatSize'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { useObjectRecoveryChain } from '../hooks'
import type { RecoveryChainTier } from '../api'

interface ObjectRecoveryChainDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string | null
}

export function ObjectRecoveryChainDialog({ open, onOpenChange, objectID }: ObjectRecoveryChainDialogProps) {
    const { t } = useTranslation()
    const { data: tiers, isLoading } = useObjectRecoveryChain(open ? objectID : null)
    const [jsonDialog, setJsonDialog] = useState<{ title: string; content: string | null } | null>(null)

    const handleShowMetadata = useCallback((title: string, content: string | null) => {
        setJsonDialog({ title, content })
    }, [])

    const columns: ColumnDef<RecoveryChainTier>[] = useMemo(() => [
        {
            accessorKey: 'tier',
            header: t('recoverRequests.tier'),
            cell: ({ row }) => <span className="text-sm">{(row.getValue('tier') as string | null) || '-'}</span>,
        },
        {
            accessorKey: 'analytics',
            header: () => <div className="text-center w-full">{t('recoverRequests.analytics')}</div>,
            cell: ({ row }) => (
                <div className="flex items-center justify-center">
                    <AnalyticsStatusIndicator status={row.getValue('analytics') as number | null} size="h-4 w-4" />
                </div>
            ),
        },
        {
            accessorKey: 'size',
            header: () => <div className="text-right w-full">{t('recoverRequests.size')}</div>,
            cell: ({ row }) => {
                const size = row.getValue('size') as number | null
                if (size === null || size === undefined) return <div className="text-right text-muted-foreground">-</div>
                return <div className="text-right"><span className="text-sm whitespace-nowrap">{formatBytes(size)}</span></div>
            },
        },
        {
            accessorKey: 'metadata',
            header: t('recoverRequests.metadata'),
            cell: ({ row }) => {
                const metadata = row.getValue('metadata') as string | null
                if (!metadata) return <span className="text-muted-foreground">-</span>
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1"
                        onClick={() => handleShowMetadata(t('recoverRequests.metadata'), metadata)}
                    >
                        <FileJson className="h-3.5 w-3.5" />
                    </Button>
                )
            },
        },
    ], [t, handleShowMetadata])

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-[1000px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {t('objects.recoveryChain', 'Recovery Chain')} - {objectID}
                        </DialogTitle>
                        <DialogDescription>
                            {t('objects.recoveryChainDescription', 'The recovery chain tiers for this object')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="text-muted-foreground">{t('recoverRequests.loading', 'Loading...')}</div>
                            </div>
                        ) : !tiers || tiers.length === 0 ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="text-muted-foreground">{t('objects.noRecoveryChain', 'No recovery chain for this object')}</div>
                            </div>
                        ) : (
                            <DataTable columns={columns} data={tiers} enableRowSelection={false} hideSearch={true} />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {jsonDialog && (
                <JsonViewDialog
                    open={!!jsonDialog}
                    onOpenChange={(o) => { if (!o) setJsonDialog(null) }}
                    title={jsonDialog.title}
                    content={jsonDialog.content}
                />
            )}
        </>
    )
}
